import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Pause, Play, Stop, ArrowUUpLeft, MapPin } from '@phosphor-icons/react';
import TrekMap from '../components/TrekMap';
import GpsPill from '../components/GpsPill';
import OfflineBadge from '../components/OfflineBadge';
import { StatTile } from '../components/StatTile';
import WaypointDialog, { categoryMeta } from '../components/WaypointDialog';
import { GpsTracker } from '../lib/gps';
import { getSetting, saveTrek } from '../lib/db';
import { buildWalkingRoute } from '../lib/simulator';
import { totalDistance, fmtDistance, fmtDuration } from '../lib/geo';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function ActiveTrek() {
  const nav = useNavigate();
  const [points, setPoints] = useState([]);
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState('starting'); // starting|running|paused
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [waypoints, setWaypoints] = useState([]);
  const [wpOpen, setWpOpen] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [pauseAcc, setPauseAcc] = useState(0);
  const [pausedAt, setPausedAt] = useState(null);
  const [simMode, setSimMode] = useState(false);
  const trackerRef = useRef(null);
  const trekIdRef = useRef(`trek-${Date.now()}`);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const interval = (await getSetting('intervalMs', 3000)) || 3000;
      const sim = await getSetting('simulator', false);
      if (!mounted) return;
      setSimMode(!!sim);

      const tracker = new GpsTracker({
        intervalMs: interval,
        onPoint: (p, meta) => {
          setCurrent(p);
          if (meta && meta.record) {
            setPoints((prev) => [...prev, p]);
          }
        },
        onError: (err) => {
          toast.error(`GPS error: ${err.message || err.code}`);
        },
      });
      trackerRef.current = tracker;
      setStartedAt(Date.now());
      setStatus('running');

      if (sim) {
        // Try to seed simulator near current real GPS; fall back to Everest coords.
        let seed = { lat: 27.9881, lng: 86.9250 };
        try {
          const one = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(
              (pos) => res({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              (e) => rej(e), { timeout: 3000 }
            ));
          seed = one;
        } catch {}
        const route = buildWalkingRoute(seed, { steps: 200, stepMeters: 6, seed: Date.now() % 100 });
        tracker.startSimulator(route, { speedFactor: 4 });
        toast('Simulator running', { description: 'Virtual walk started.' });
      } else {
        tracker.startReal();
      }
    })();
    return () => {
      mounted = false;
      trackerRef.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'running') return;
    const id = setInterval(() => {
      setElapsed(Date.now() - startedAt - pauseAcc);
    }, 500);
    return () => clearInterval(id);
  }, [status, startedAt, pauseAcc]);

  const distanceTravelled = useMemo(() => totalDistance(points), [points]);

  const doPause = () => {
    trackerRef.current?.pause();
    setPausedAt(Date.now());
    setStatus('paused');
  };
  const doResume = () => {
    if (pausedAt) setPauseAcc((a) => a + (Date.now() - pausedAt));
    setPausedAt(null);
    trackerRef.current?.resume();
    setStatus('running');
  };

  const doEnd = async () => {
    trackerRef.current?.stop();
    const now = Date.now();
    if (points.length < 2) {
      toast.error('Not enough points recorded to save trek.');
      nav('/');
      return;
    }
    const trek = {
      id: trekIdRef.current,
      name: `Trek ${new Date(startedAt).toLocaleString()}`,
      startedAt,
      endedAt: now,
      points,
      waypoints,
      status: 'ended',
      isDemo: false,
      simulated: simMode,
    };
    await saveTrek(trek);
    toast.success('Trek saved', { description: `${fmtDistance(distanceTravelled)} · ${points.length} points` });
    nav(`/treks/${trek.id}`);
  };

  return (
    <div className="min-h-[100dvh] flex flex-col relative">
      <header className="safe-top px-4 pt-3 pb-2 flex items-center justify-between z-10">
        <button onClick={() => setConfirmEnd(true)} data-testid="back-active" className="btn-tap glass-panel rounded-full p-2">
          <ArrowUUpLeft size={18} />
        </button>
        <div className="flex items-center gap-2">
          <GpsPill accuracy={current?.accuracy} />
          <OfflineBadge />
        </div>
      </header>

      <div className="flex-1 px-3">
        <TrekMap
          points={points}
          current={current}
          follow
          waypoints={waypoints.map((w) => ({ ...w, color: categoryMeta(w.category).color }))}
          height="100%"
        />
      </div>

      {/* Stats panel */}
      <div className="glass-panel mx-3 mt-3 rounded-2xl p-3 z-10">
        <div className="grid grid-cols-3 gap-2">
          <StatTile testId="stat-duration" label="Time" value={fmtDuration(elapsed)} />
          <StatTile testId="stat-distance" label="Distance" value={fmtDistance(distanceTravelled)} />
          <StatTile testId="stat-elevation" label="Elevation" value={current?.altitude != null ? Math.round(current.altitude) : '—'} unit={current?.altitude != null ? 'm' : ''} />
          <StatTile testId="stat-accuracy" label="Accuracy" value={current?.accuracy != null ? `±${Math.round(current.accuracy)}` : '—'} unit="m" />
          <StatTile testId="stat-heading" label="Heading" value={current?.heading != null && !isNaN(current.heading) ? `${Math.round(current.heading)}°` : '—'} />
          <StatTile testId="stat-points" label="Points" value={points.length} />
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          {status !== 'paused' ? (
            <button data-testid="pause-trek" onClick={doPause} className="btn-tap col-span-1 h-12 rounded-xl bg-white/10 hover:bg-white/15 flex items-center justify-center gap-1.5 font-semibold text-sm">
              <Pause size={18} weight="fill" /> PAUSE
            </button>
          ) : (
            <button data-testid="resume-trek" onClick={doResume} className="btn-tap col-span-1 h-12 rounded-xl bg-[#06D6A0]/15 border border-[#06D6A0]/40 text-[#06D6A0] flex items-center justify-center gap-1.5 font-semibold text-sm">
              <Play size={18} weight="fill" /> RESUME
            </button>
          )}
          <button
            data-testid="drop-waypoint"
            onClick={() => setWpOpen(true)}
            disabled={!current}
            className="btn-tap col-span-1 h-12 rounded-xl bg-[#FFD166]/15 border border-[#FFD166]/40 text-[#FFD166] flex items-center justify-center gap-1.5 font-semibold text-sm disabled:opacity-40"
          >
            <MapPin size={18} weight="fill" /> PIN
          </button>
          <button
            data-testid="end-trek"
            onClick={() => setConfirmEnd(true)}
            className="btn-tap col-span-1 h-12 rounded-xl bg-[#EF476F] text-white flex items-center justify-center gap-1.5 font-display font-bold tracking-wider"
          >
            <Stop size={20} weight="fill" /> END
          </button>
        </div>
        {waypoints.length > 0 && (
          <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-400 text-center" data-testid="waypoint-count">
            {waypoints.length} waypoint{waypoints.length === 1 ? '' : 's'} dropped
          </div>
        )}
        {simMode && (
          <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-300/80 text-center">Simulator mode</div>
        )}
      </div>

      <div className="h-4 safe-bottom" />

      <WaypointDialog
        open={wpOpen}
        onOpenChange={setWpOpen}
        currentPosition={current}
        onSave={(wp) => {
          setWaypoints((prev) => [...prev, wp]);
          setWpOpen(false);
          toast.success('Waypoint dropped', { description: wp.name });
        }}
      />

      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent data-testid="end-dialog" className="bg-[#141414] border-white/10 max-w-[92%] rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">End trek?</AlertDialogTitle>
            <AlertDialogDescription>Your route will be saved locally so you can use Return to Start later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-end" className="bg-white/5 border-white/10">Keep trekking</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-end" onClick={doEnd} className="bg-[#FF9F1C] text-black hover:bg-[#FFB347]">End &amp; save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
